import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAccessibleModules } from '../../../hooks/useAccessibleModules';
import styles from './ModuleSwitcher.module.css';

type ModuleSwitcherProps = {
  isOpen: boolean;
  onClose: () => void;
};

function activeKey(pathname: string): 'collections' | 'skip' | 'weighbridge' | null {
  if (pathname.startsWith('/collections')) return 'collections';
  if (pathname.startsWith('/skip')) return 'skip';
  if (pathname.startsWith('/weighbridge')) return 'weighbridge';
  return null;
}

export function ModuleSwitcher({ isOpen, onClose }: ModuleSwitcherProps) {
  const modules = useAccessibleModules();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = activeKey(pathname);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.headerLabel}>Switch Module</span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className={styles.grid}>
          {modules.map((mod) => {
            const isActive = mod.key === current;
            return (
              <button
                key={mod.key}
                type="button"
                className={isActive ? `${styles.card} ${styles.cardActive}` : styles.card}
                onClick={isActive ? undefined : () => { navigate(mod.to); onClose(); }}
                tabIndex={isActive ? -1 : 0}
                aria-current={isActive ? 'page' : undefined}
              >
                <mod.icon
                  size={28}
                  aria-hidden
                  className={isActive ? styles.iconActive : styles.icon}
                />
                <span className={styles.cardLabel}>{mod.label}</span>
                <span className={styles.cardDescription}>{mod.description}</span>
                {isActive && <span className={styles.currentBadge}>Current</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
